import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import pages from './search-pages.json';

const SITE = 'https://gethaphe.com';
type SearchPage = { name: string; title: string; description: string; questions: string[][] };
const pageMap: Record<string, SearchPage> = pages;

export function MarketingSeo() {
  const { pathname } = useLocation();
  const page = pageMap[pathname];
  useEffect(() => {
    if (!page) return;
    const previousTitle = document.title;
    const cleanups: (() => void)[] = [];
    const set = (selector: string, tag: string, attrs: Record<string, string>) => {
      const existing = document.head.querySelector(selector);
      const el = existing || document.createElement(tag);
      const previous = el.outerHTML;
      Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, value));
      if (!existing) document.head.appendChild(el);
      cleanups.push(() => { if (existing) el.outerHTML = previous; else el.remove(); });
    };
    document.title = page.title;
    const canonical = `${SITE}${pathname === '/' ? '/' : pathname}`;
    set('link[rel="canonical"]', 'link', { rel: 'canonical', href: canonical });
    const meta = (key: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      set(`meta[${attr}="${key}"]`, 'meta', { [attr]: key, content });
    };
    meta('description', page.description);
    meta('robots', 'index, follow, max-image-preview:large');
    meta('og:title', page.title, true); meta('og:description', page.description, true);
    meta('og:url', canonical, true); meta('og:type', 'website', true); meta('og:site_name', 'Haphe', true);
    meta('og:image', `${SITE}/og.png`, true); meta('og:image:alt', 'Haphe — digital business cards and connections', true);
    meta('twitter:card', 'summary_large_image'); meta('twitter:title', page.title);
    meta('twitter:description', page.description); meta('twitter:image', `${SITE}/og.png`);
    const graph: Record<string, unknown>[] = [
      { '@type': 'Organization', '@id': `${SITE}/#organization`, name: 'Haphe', url: SITE },
      { '@type': 'WebSite', '@id': `${SITE}/#website`, name: 'Haphe', url: SITE, publisher: { '@id': `${SITE}/#organization` } },
      { '@type': 'WebPage', '@id': `${canonical}#webpage`, url: canonical, name: page.title, description: page.description, isPartOf: { '@id': `${SITE}/#website` }, inLanguage: 'en' },
    ];
    if (pathname !== '/') graph.push({ '@type': 'BreadcrumbList', itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` }, { '@type': 'ListItem', position: 2, name: page.name, item: canonical }] });
    if (page.questions.length) graph.push({ '@type': 'FAQPage', '@id': `${canonical}#questions`, mainEntity: page.questions.map(([question, answer]) => ({ '@type': 'Question', name: question, acceptedAnswer: { '@type': 'Answer', text: answer } })) });
    const structured = document.createElement('script');
    structured.type = 'application/ld+json'; structured.id = 'haphe-search-schema';
    // Replace the serialized copy when mounting on a pre-rendered page.
    document.getElementById(structured.id)?.remove();
    structured.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
    document.head.appendChild(structured);
    return () => { document.title = previousTitle; cleanups.reverse().forEach(cleanup => cleanup()); structured.remove(); };
  }, [pathname, page]);
  return null;
}

export function SearchAnswers() {
  const { pathname } = useLocation();
  const page = pageMap[pathname];
  if (!page?.questions.length) return null;
  return <section className="search-answers" id="questions" aria-labelledby="questions-title"><div><p className="mk-kicker">A little clarity</p><h2 id="questions-title">Good questions.<br/><em>Simple answers.</em></h2><p>Get to know what your Haphe can do.</p><Link to="/pricing">Compare plans →</Link></div><div>{page.questions.map(([question, answer]) => <details key={question}><summary>{question}</summary><p>{answer}</p></details>)}</div></section>;
}
